/**
 * Offline-first photo upload queue for Vehicle Check-In.
 * Uses IndexedDB to persist unsent photos, drains automatically when online.
 */
const DB_NAME = "wtd-crm-photos";
const STORE = "queue";
const VERSION = 1;

function openDB() {
  return new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE)) {
        const s = db.createObjectStore(STORE, { keyPath: "localId" });
        s.createIndex("inspection_id", "inspection_id");
        s.createIndex("status", "status");
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

async function tx(mode, fn) {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const t = db.transaction(STORE, mode);
    const s = t.objectStore(STORE);
    const r = fn(s);
    t.oncomplete = () => resolve(r);
    t.onerror = () => reject(t.error);
    t.onabort = () => reject(t.error);
  });
}

export async function enqueuePhoto({ inspectionId, kind, blob, mime }) {
  const localId = `local_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
  const entry = { localId, inspection_id: inspectionId, kind, blob, mime, status: "pending", created_at: new Date().toISOString(), attempts: 0 };
  await tx("readwrite", (s) => s.put(entry));
  return entry;
}

export async function listQueue(inspectionId) {
  return new Promise(async (resolve) => {
    const db = await openDB();
    const t = db.transaction(STORE, "readonly");
    const idx = t.objectStore(STORE).index("inspection_id");
    const req = idx.getAll(inspectionId);
    req.onsuccess = () => resolve(req.result || []);
    req.onerror = () => resolve([]);
  });
}

export async function markUploaded(localId) {
  await tx("readwrite", (s) => s.delete(localId));
}

export async function markFailed(localId, error) {
  const existing = await new Promise(async (resolve) => {
    const db = await openDB();
    const t = db.transaction(STORE, "readonly");
    const req = t.objectStore(STORE).get(localId);
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => resolve(null);
  });
  if (!existing) return;
  existing.status = "failed";
  existing.attempts = (existing.attempts || 0) + 1;
  existing.last_error = String(error).slice(0, 240);
  await tx("readwrite", (s) => s.put(existing));
}

/**
 * Compress a File/Blob to JPEG < ~1MB by scaling longest side to 1600px, quality 0.82.
 * Returns a new Blob.
 * createImageBitmap is missing on Safari < 15 (and throws synchronously there —
 * a plain .catch() cannot intercept a ReferenceError), so fall back to an
 * HTMLImageElement decode path for legacy browsers (studio iPad on iOS 12).
 */
export async function compressImage(file, maxDim = 1600, quality = 0.82) {
  if (!file || !file.type?.startsWith("image/")) return file;

  let src = null;
  let w0 = 0;
  let h0 = 0;
  let objUrl = null;

  if (typeof createImageBitmap === "function") {
    const bitmap = await createImageBitmap(file).catch(() => null);
    if (bitmap) { src = bitmap; w0 = bitmap.width; h0 = bitmap.height; }
  }
  if (!src) {
    // Legacy path: decode through an <img> element
    objUrl = URL.createObjectURL(file);
    try {
      const img = await new Promise((res, rej) => {
        const i = new Image();
        i.onload = () => res(i);
        i.onerror = () => rej(new Error("image decode failed"));
        i.src = objUrl;
      });
      if (!img.naturalWidth) throw new Error("image decode failed");
      src = img; w0 = img.naturalWidth; h0 = img.naturalHeight;
    } catch (e) {
      URL.revokeObjectURL(objUrl);
      return file;
    }
  }

  const scale = Math.min(1, maxDim / Math.max(w0, h0));
  const w = Math.max(1, Math.round(w0 * scale));
  const h = Math.max(1, Math.round(h0 * scale));
  const canvas = document.createElement("canvas");
  canvas.width = w; canvas.height = h;
  canvas.getContext("2d").drawImage(src, 0, 0, w, h);
  if (objUrl) URL.revokeObjectURL(objUrl);
  return await new Promise((res) => canvas.toBlob((b) => res(b || file), "image/jpeg", quality));
}

/**
 * Attempt to upload a queued entry now.
 * Returns { ok, photo } or { ok:false, error }.
 */
export async function uploadOne(entry, backendUrl) {
  const fd = new FormData();
  fd.append("kind", entry.kind);
  fd.append("file", entry.blob, `${entry.kind}.jpg`);
  const res = await fetch(`${backendUrl}/api/crm/inspections/${entry.inspection_id}/photos`, {
    method: "POST",
    body: fd,
    credentials: "include",
  });
  if (!res.ok) {
    const text = await res.text().catch(() => "");
    throw new Error(text || `HTTP ${res.status}`);
  }
  return await res.json();
}

/**
 * Drain queue for an inspection. Called on `online` event and after each add.
 * `onProgress(count)` called after each successful upload.
 */
export async function drainQueue(inspectionId, backendUrl, onProgress) {
  const items = await listQueue(inspectionId);
  const pending = items.filter((i) => i.status !== "uploaded");
  for (const it of pending) {
    if (!navigator.onLine) return;
    try {
      const photo = await uploadOne(it, backendUrl);
      await markUploaded(it.localId);
      onProgress?.(photo);
    } catch (e) {
      await markFailed(it.localId, e?.message || e);
    }
  }
}
